import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {catchError, map, Observable} from 'rxjs';
import {GridGetService, GridServiceParams, GridServiceResponse} from 'src/app/shared/model/page-grid';
import {removeEmptyParams} from 'src/infrastructure/helper/remove-empty-params';
import {ExportPdfService} from 'src/app/shared/service/files/export-pdf.service';
import {AdxPaymentService} from './adx-payment.service';
import {ReconciliationModel} from '../models/client-reconciliation';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class ClientPaymentsReconciliationGridService implements GridGetService<ReconciliationModel> {
  private readonly http = inject(HttpClient);
  private readonly pdfService = inject(ExportPdfService);
  private readonly adxPaymentService = inject(AdxPaymentService);

  getItems(params: GridServiceParams): Observable<GridServiceResponse<ReconciliationModel>> {
    return this.adxPaymentService.getReconciliation(params);
  }

  getPdf({ filters, page, sort }: GridServiceParams): Observable<void> {
    const storedFilter = this.adxPaymentService.getPaymentsReconciliationFilter();

    let effectiveFilters: any = { processorIds: [1, 2] };

    const currentStoredFilter = (storedFilter as any).source?._value || {};
    if (currentStoredFilter && Object.keys(currentStoredFilter).length > 0) {
      Object.assign(effectiveFilters, currentStoredFilter);
    }

    if (filters && Object.keys(filters).length > 0) {
      Object.keys(filters).forEach(key => {
        const filterValue = filters[key];
        if (filterValue !== null && filterValue !== undefined) {
          if ((key !== 'adxReference' && key !== 'lastFourCC') || !effectiveFilters[key]) {
            effectiveFilters[key] = filterValue;
          }
        }
      });
    }

    const params: Record<string, string | number | boolean | any> = {
      PageNumber: (page?.pageIndex || 0) + 1,
      PageSize: page?.pageSize || 100,
      PropertyName: sort?.active || '',
      OrderKey: sort?.direction?.toUpperCase() || '',

      exportToPdf: true,
      format: 'pdf',
      formatCurrency: 'true',
      hideCurrencySymbol: 'true',
      currencyCode: 'USD',
      currencySymbol: '$',
      currencyFormat: '0.00'
    };

    if (effectiveFilters) {
      Object.keys(effectiveFilters).forEach(key => {
        const value = effectiveFilters[key];
        if (value !== null && value !== undefined) {
          if (key === 'processorIds' && Array.isArray(value)) {
            params[key] = value;
          } else if (key === 'adxReference') {
            if (typeof value === 'object' && value !== null) {
              const adxRef = value as any;
              if (adxRef.value) {
                params[key] = String(adxRef.value);
              } else if (adxRef.toString) {
                params[key] = adxRef.toString();
              }
            } else if (value !== null) {
              params[key] = String(value);
            }
          } else if (key === 'lastFourCC') {
            if (typeof value === 'object' && value !== null) {
              const lastFour = value as any;
              if (lastFour.value) {
                params[key] = String(lastFour.value);
              } else if (lastFour.toString) {
                params[key] = lastFour.toString();
              }
            } else if (value !== null) {
              params[key] = String(value);
            }
          } else {
            params[key] = value;
          }
        }
      });

      if (effectiveFilters.isMatched !== undefined && effectiveFilters.isMatched !== null) {
        params['isMatched'] = effectiveFilters.isMatched;
      }

      if (effectiveFilters.releaseDateFrom) {
        params['releaseDateFrom'] = effectiveFilters.releaseDateFrom;
      }

      if (effectiveFilters.releaseDateTo) {
        params['releaseDateTo'] = effectiveFilters.releaseDateTo;
      }
    }

    if (params['releaseDateFrom'] && typeof params['releaseDateFrom'] === 'string') {
      params['releaseDateFrom'] = moment(params['releaseDateFrom'] as string).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    }
    if (params['releaseDateTo'] && typeof params['releaseDateTo'] === 'string') {
      params['releaseDateTo'] = moment(params['releaseDateTo'] as string).format('YYYY-MM-DDTHH:mm:ss.SSS[Z]');
    }

    removeEmptyParams(params);

    let url = 'dbov2/payment-reconciliation/recon-search';
    if (params['releaseDateFrom'] || params['releaseDateTo']) {
      url = 'dbov2/payment-reconciliation/recon-search-extended';
    }

    const httpParams: Record<string, string | number | boolean | string[] | number[]> = {};
    Object.keys(params).forEach(key => {
      const value = params[key];
      if (Array.isArray(value)) {
        httpParams[key] = value;
      } else {
        httpParams[key] = value;
      }
    });

    return this.http.get<{ pdfBase64: string }>(url, { params: httpParams })
      .pipe(
        map((response) => {
          if (response && (response as any).pdfBase64) {
            const timestamp = new Date().toISOString().replace(/[-:T]/g, '').split('.')[0];
            const fileName = `payments-reconciliation_${timestamp}.pdf`;
            this.pdfService.savePdfFromBase64((response as any).pdfBase64, fileName);
          } else {
            console.warn('PDF export not implemented by backend. Response:', response);
            alert('PDF export is not yet available for Payment Reconciliation.\n\nThe backend API needs to be updated to:\n1. Add /export-pdf endpoint, OR\n2. Return PDF base64 data when exportToPdf=true\n\nPlease contact the development team to implement this functionality.');
          }
          return;
        }),
        catchError((error: HttpErrorResponse) => {
          console.error('Error requesting PDF export:', error);
          if (error.status === 404) {
            alert('PDF export endpoint not found. The backend needs to implement PDF generation for Payment Reconciliation.');
          } else {
            alert('PDF export failed. Please try again or contact support if the issue persists.');
          }
          throw new Error(error?.error || 'PDF download failed');
        })
      );
  }
}
